import { Injectable } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

interface StorageChange {
	key: string;
	newValue: string | null;
	oldValue: string | null;
	crossTab: boolean;
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
	// Emits custom storage events within the same tab
	static readonly currentTabChanges$ = new Subject<StorageChange>();

	// Merges native 'storage' events and custom events, shared across subscribers
	static readonly storageChanges$ = merge(
		fromEvent<StorageEvent>(window, 'storage').pipe(
			map(
				(event: StorageEvent) =>
					({
						key: event.key,
						newValue: event.newValue,
						oldValue: event.oldValue,
						crossTab: true
					}) as StorageChange
			)
		),
		LocalStorageService.currentTabChanges$
	).pipe(shareReplay(1));

	static getItem(key: string): any {
		return JSON.parse(localStorage.getItem(key));
	}

	static setItem(key: string, item: any): void {
		const oldValue = localStorage.getItem(key);
		const itemStr = JSON.stringify(item);
		localStorage.setItem(key, itemStr);
		this.currentTabChanges$.next({
			key,
			newValue: itemStr,
			oldValue,
			crossTab: false
		});
	}

	static removeItem(key: string): void {
		const oldValue = localStorage.getItem(key);
		localStorage.removeItem(key);
		this.currentTabChanges$.next({
			key,
			newValue: null,
			oldValue,
			crossTab: false
		});
	}

	static clear(): void {
		localStorage.clear();
	}
}
